// @flow
import React, { PureComponent, Fragment } from 'react';
import { Redirect } from 'react-router-dom';
import * as DOMPurify from 'dompurify';

import { withHelmet } from 'infrastructure/seo/Helmet';
import type { EventType } from 'types/Event';
import Breadcrumb from 'components/fragments/Breadcrumb';
import type { Status } from 'types/Status';
import { STATUS_DEFAULT, STATUS_FAILURE, STATUS_LOADING, STATUS_UNKNOWN } from 'constants/statusConstants';
import { getFullDate, getDateFromString, formatHours } from 'utils/dateUtils';
import DynamicEventsCardContainer from 'containers/event/DynamicEventsCardContainer';
import FooterSocial from 'components/footer/FooterSocial';
import Loading from 'components/fragments/Loading';
import ClubAdress from 'components/club/ClubAdress';
import { convertEncodedHTML } from 'utils/convertUtils';
import { isFfrEvent } from 'utils/eventUtils';
import { handleTagEvent } from 'utils/tagManagerUtils';

/**
 * $FlowFixMe
 * Original lib -> https://github.com/nwcell/ics.js
 * But ... no package and module export
 * "Open source" 😒
 */
import ics from 'libs/ics';

export type StateProps = {
  event: EventType,
  events: EventType[],
  status: Status
};

export type DispatchProps = {
  fetchEvent: (slug: string) => void,
  eventFetched: (event: EventType) => void
};

type Props = {
  match: {
    params: {
      slug: string
    }
  }
} & StateProps &
  DispatchProps;

class EventDetail extends PureComponent<Props> {
  icsGenerator = ics();

  componentDidMount() {
    const cachedEvent = this.props.events.find(event => event.slug === this.props.match.params.slug);
    if (cachedEvent) this.props.eventFetched(cachedEvent);

    this.props.fetchEvent(this.props.match.params.slug);
  }

  componentDidUpdate(prevProps: Props) {
    const { slug } = this.props.match.params;
    if (prevProps.match.params.slug !== slug) {
      const cachedEvent = this.props.events.find(event => event.slug === slug);
      if (cachedEvent) this.props.eventFetched(cachedEvent);
      this.props.fetchEvent(slug);
    }
  }

  createDescription = () => {
    return { __html: this.props.event.description };
  };

  clickTagManager = (socialName: string) => {
    const { title } = this.props.event;
    return handleTagEvent('evenement', `clic_partage ${socialName}`, title);
  };

  renderDownloadCalendar = () => {
    const { title, description, adress, startDate, endDate } = this.props.event;

    this.icsGenerator && this.icsGenerator.addEvent(title, description, adress, startDate, endDate);

    const download = () => (event: MouseEvent) => this.icsGenerator.download();

    return (
      <span onClick={download()} className="btn btn--white btn--full">
        <i className="icon icon-calendar is-inline" />
        <span>Ajouter à mon calendrier</span>
      </span>
    );
  };

  renderMap = () => {
    const { title, lat, long, venue, adress, zip, city, organizer } = this.props.event;

    const mapAdress = {
      postalCode: zip,
      street: adress,
      locality: city,
      complement: ''
    };

    if (lat && long) {
      return (
        <div className="address mb-6 mb-md-7">
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${adress}+${zip}+${city}`}
            title={`Voir l'itinéraire vers ${organizer} sur Google Maps (nouvel onglet)`}
            className="box card card--address"
            target="_blank"
            rel="noopener noreferrer"
            style={{ zIndex: 60 }}
          >
            <div className="card__body">
              <h3 className="card__title ft-h3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(organizer) }} />
              <p className="card__desc">
                {convertEncodedHTML(venue)}
                <br />
                {adress},<br />
                {zip} {city}
              </p>
            </div>
            <div className="card__footer">
              <span className="link-chevron ">
                {"Voir l'itinéraire"}
                <i className="icon icon-chevron-right" />
              </span>
            </div>
          </a>
          <ClubAdress hideIndicator lat={lat} long={long} name={title} adress={mapAdress} />
        </div>
      );
    }

    return null;
  };

  renderContact = () => {
    const { email, phone, organizer } = this.props.event;

    if (email || phone) {
      return (
        <Fragment>
          <h3 className="mb-3">Contacter l’organisateur</h3>
          <div className="contact mb-6 mb-md-7">
            <div className="box">
              <ul className="contact__club">
                {email && (
                  <li className="contact__item">
                    <i className="icon icon-mail" />
                    <p className="ft-h4">Adresse email</p>
                    <p>
                      <a
                        href={`mailto:${email}`}
                        title={`Envoyer un email à ${organizer}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {email}
                      </a>
                    </p>
                  </li>
                )}
                {phone && (
                  <li className="contact__item">
                    <i className="icon icon-phone" />
                    <p className="ft-h4">Téléphone</p>
                    <p>
                      <a
                        href={`tel:${phone}`}
                        title={`Contacter ${organizer} par téléphone`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {phone}
                      </a>
                    </p>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Fragment>
      );
    }
    return null;
  };

  render() {
    const { status } = this.props;
    if (status === STATUS_DEFAULT || status === STATUS_LOADING) {
      return <Loading />;
    } else if (status === STATUS_FAILURE || status === STATUS_UNKNOWN) {
      return <Redirect to="/404" />;
    }

    let {
      label,
      title,
      startDate,
      endDate,
      mediumImage,
      description,
      all_day,
      redirection,
      categories
    } = this.props.event;

    if (isFfrEvent(this.props.event)) {
      if (redirection) {
        window.location.replace(redirection);
      } else if (categories && categories.length > 0) {
        label = categories.reduce((acc, category) => acc + (acc ? ' - ' : '') + category.name, '');
      }
    }

    const start = getDateFromString(startDate);
    const end = getDateFromString(endDate);
    const eventDates =
      start.getMonth() !== end.getMonth() || start.getDay() !== end.getDay()
        ? `${getFullDate(start)} - ${getFullDate(end)}`
        : getFullDate(start);

    return (
      <div className="container">
        <div className="row">
          <div className="col col-md-8 col-lg-9 bg-overflow">
            <section>
              <div className="row">
                <div className="col col-lg-8on9">
                  <Breadcrumb
                    elements={[
                      { name: 'accueil', to: '/' },
                      { name: 'événements', to: '/evenements' },
                      { name: title, to: '#' }
                    ]}
                    breadcrumbStyle="mt-3"
                  />
                  <div className="event-single mt-6 mb-6 mb-md-4 mb-lg-7">
                    {label && <span className="label mb-2">{label}</span>}
                    <h1 className="mb-2 mb-md-3">{title}</h1>
                    <ul className="mb-4 event-single__info">
                      <li>
                        <i className="icon icon-calendar is-inline" />
                        <strong>{eventDates}</strong>
                      </li>
                      {!all_day && (
                        <li>
                          <i className="icon icon-clock is-inline" />
                          {formatHours(start)}
                          {start.getTime() !== end.getTime() && ` - ${formatHours(end)}`}
                        </li>
                      )}
                    </ul>
                    <div className="event-single__content">
                      <div>
                        <div style={{ display: 'flex' }}>
                          {mediumImage && <img src={mediumImage} alt="" style={{ marginRight: '24px' }} />}
                          <div dangerouslySetInnerHTML={{ __html: description }} />
                        </div>
                        <div>{this.renderDownloadCalendar()}</div>
                      </div>
                    </div>
                  </div>
                  {this.renderMap()}
                  {this.renderContact()}
                  <FooterSocial
                    url={window.location.href}
                    title={title ? title : ''}
                    titleclass="mb-3"
                    clickTagManager={socialName => this.clickTagManager(socialName)}
                  />
                </div>
              </div>
            </section>
          </div>
          <div className="col col-md-4 col-lg-3">
            <aside className="sidebar sidebar--alt">
              <h3 className="ft-400">Événements à venir</h3>
              <div className="box mb-4 mb-md-5">
                <DynamicEventsCardContainer limit={2} />
              </div>
            </aside>
          </div>
        </div>
      </div>
    );
  }
}

export default withHelmet(EventDetail)((Helmet, pageProps) => (
  <Helmet>
    <title>{pageProps.event.title}</title>
    <meta property={'og:title'} content={pageProps.event.title} />
    <link rel={'canonical'} href={window.location.href} />
  </Helmet>
));
